import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ApiService from "../../api/ApiServices";
import LoadImage from "../../assets/loader.gif";

const DEFAULT_PAGE_SIZE = 10;

type PointHistoryItem = {
	id: string;
	title: string;
	points: number;
	createdAt: string;
	icon?: string;
};

type PointHistoryGroup = {
	date: string;
	items: PointHistoryItem[];
};

type PaginationData = {
	totalItems: number;
	currentPage: number;
	totalPages: number;
	pageSize: number;
};

const getItemArrayFromGroup = (group: any): any[] => {
	if (Array.isArray(group?.history)) {
		return group.history;
	}
	return [];
};

const extractHistoryItems = (data: any): any[] => {
	const source = data?.history;

	if (!Array.isArray(source)) {
		return [];
	}

	if (source.length === 0) {
		return [];
	}

	const firstItem = source[0];
	if (Array.isArray(firstItem?.history) || firstItem?.date) {
		return source.flatMap((group: any) => getItemArrayFromGroup(group));
	}

	return source;
};

const getTitleFromItem = (item: any): string => {
    
	if (item?.type === 'CHALLENGE_ATTEMPT') {
		return item?.challengeId?.title || 'Challenge Attempt';
	}

    if (item?.type === 'STREAK_BONUS') {
		return 'Streak Week Points';    
	}

    if (item?.type === 'DAILY_CHECKIN') {
		return 'Daily Check-ins';    
	}

    if (item?.type === 'HOUSE_WIN') {
		return 'House Points Bonus ';    
	}

	return item?.title || '';
};


const getDateFromItem = (item: any): string => {
	return (
		item?.createdAt ||
		item?.timestamp ||
		new Date().toISOString()
	);
};

const getIcon = (item: any): string => {
	
    if (item?.type === 'CHALLENGE_ATTEMPT') {
        if(item?.challengeId?.pillar === 'Environment' || item?.challengeId?.pillar === 'environment') {
            return require("../../assets/ic_tree.png");
        }

        if(item?.challengeId?.pillar === 'Culture' || item?.challengeId?.pillar === 'culture') {
            return require("../../assets/ic_food.png");
        }

        if(item?.challengeId?.pillar === 'Self' || item?.challengeId?.pillar === 'self') {
            return require("../../assets/ic_picPost.png");
        }

        if(item?.challengeId?.pillar === 'Connection' || item?.challengeId?.pillar === 'connection') {
            return require("../../assets/ic_userBlue.png");
        }
		return require("../../assets/star.png");
	}

    if (item?.type === 'STREAK_BONUS') {
		return require("../../assets/fire.png");    
	}

    if (item?.type === 'DAILY_CHECKIN') {
		return require("../../assets/green-checkin.png");    
	}

    if (item?.type === 'HOUSE_WIN') {
		return require("../../assets/house.png");    
	}

	return require("../../assets/star.png");
};

const normalizePointsHistory = (items: any[]): PointHistoryGroup[] => {
	if (!Array.isArray(items)) {
		return [];
	}

	const groupedByDate: Record<string, PointHistoryItem[]> = {};

	items.forEach((item: any, index: number) => {
		const title = getTitleFromItem(item);
		const itemDate = getDateFromItem(item);
		const key = moment(itemDate).format("YYYY-MM-DD");

		if (!groupedByDate[key]) {
			groupedByDate[key] = [];
		}

		groupedByDate[key].push({
			id: String(item?._id || item?.id || index),
			title,
			points: item?.points ?? 0,
			createdAt: itemDate,
			icon: item?.icon || item?.image || getIcon(item),
		});
	});

	return Object.keys(groupedByDate)
		.sort((a, b) => moment(b).valueOf() - moment(a).valueOf())
		.map((dateKey) => ({
			date: moment(dateKey).format("MMM D, YYYY"),
			items: groupedByDate[dateKey],
		}));
};

const MyPointHistory = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState<boolean>(false);
	const [loadMore, setLoadMore] = useState<boolean>(false);
	const [historyItems, setHistoryItems] = useState<any[]>([]);
	const [pagination, setPagination] = useState<PaginationData>({
		totalItems: 0,
		currentPage: 1,
		totalPages: 1,
		pageSize: DEFAULT_PAGE_SIZE,
	});

	const pointHistory = useMemo(() => normalizePointsHistory(historyItems), [historyItems]);

	const getPointHistory = async (pageNumber: number = 1, isLoadMore: boolean = false) => {
		const requestedPageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;

		if (isLoadMore) {
			setLoadMore(true);
		} else {
			setLoading(true);
		}

		try {
			const response = await ApiService.getPoints(pageNumber, requestedPageSize);
			const responseData = response?.data || {};
			const nextItems = extractHistoryItems(responseData);

			setHistoryItems((prevItems) => {
				const mergedItems = pageNumber === 1 ? nextItems : [...prevItems, ...nextItems];
				const uniqueMap = new Map<string, any>();

				mergedItems.forEach((item: any, index: number) => {
					const itemId = String(item?._id || item?.id || `${getDateFromItem(item)}-${item?.type || "item"}-${index}`);
					if (!uniqueMap.has(itemId)) {
						uniqueMap.set(itemId, item);
					}
				});

				return Array.from(uniqueMap.values());
			});

			const responsePagination = response?.pagination || responseData?.pagination || {};
			const resolvedPageSize = Number(responsePagination?.pageSize || responsePagination?.limit || requestedPageSize) || DEFAULT_PAGE_SIZE;
			const resolvedTotalItems = Number(responsePagination?.totalItems) || nextItems.length;
			const resolvedCurrentPage = Number(responsePagination?.currentPage || responsePagination?.page) || pageNumber;
			const resolvedTotalPages =
				Number(responsePagination?.totalPages) ||
				Math.max(1, Math.ceil(resolvedTotalItems / resolvedPageSize));

			setPagination({
				totalItems: resolvedTotalItems,
				currentPage: resolvedCurrentPage,
				totalPages: resolvedTotalPages,
				pageSize: resolvedPageSize,
			});
		} finally {
			if (isLoadMore) {
				setLoadMore(false);
			} else {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		setHistoryItems([]);
		setPagination({
			totalItems: 0,
			currentPage: 1,
			totalPages: 1,
			pageSize: DEFAULT_PAGE_SIZE,
		});
		getPointHistory(1);
	}, []);

	const onLoadMore = () => {
		if (loadMore || loading || pagination.currentPage >= pagination.totalPages) {
			return;
		}

		getPointHistory(pagination.currentPage + 1, true);
	};

	const bottomSpacing = "calc(env(safe-area-inset-bottom, 10px) + 88px)";

	return (
		<div className="min-h-screen" style={{ paddingBottom: bottomSpacing }}>
			<div className="flex items-center relative px-3 py-3 ">
				<img
					onClick={() => navigate(-1)}
					alt="Back"
					className="home-frag-pic-backbtn mr-2 cursor-pointer"
					src={require("../../assets/arrow-left.png")}
				/>
				<div className="flex items-center justify-center flex-1 btn-onb2-text">
					My Point History
				</div>
			</div>

			{loading ? (
				<div className="flex justify-center items-center h-[70vh]">
					<img width="100px" className="center" src={LoadImage} alt="loader" />
				</div>
			) : (
				<div className="px-2" style={{ paddingBottom: bottomSpacing }}>
					{pointHistory.length === 0 && (
						<div className="px-4 pt-8 text-center inter-regular-12">No point history available.</div>
					)}

					{pointHistory.map((group) => (
						<div key={group.date} className="mb-4">
							<div className="px-4 py-2 inter-text-14 text-[#4A4A4A]">{group.date}</div>

							<div className="">
								{group.items.map((item) => (
									<div
										key={item.id}
										className="px-3 py-3 border-b border-gray-300 flex items-center justify-between"
									>
										<div className="flex items-center flex-1 min-w-0 pr-2">
											<img
												src={item.icon || require("../../assets/star.png")}
												alt=""
												className="w-5 h-5 mr-2 object-contain"
											/>
											<div className="inter-text-14 truncate">{item.title}</div>
										</div>

										<div className="inter-text-14 whitespace-nowrap">{item.points} pts</div>
									</div>
								))}
							</div>
						</div>
					))}
                    
					{pagination.currentPage < pagination.totalPages && pointHistory.length > 0 && (
						<div className="flex justify-center mt-2">
							<div
								onClick={onLoadMore}
								className="btn-onb4-text underline underline-offset-4 cursor-pointer"
							>
								{loadMore ? "Loading..." : "Load More"}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default MyPointHistory;
